// src/Pages/backup/BackupAyuda.tsx
//
// Explicación (i) de /backup, para el superadministrador. Cada afirmación sale de
// docs/modulo-backup.md y de backend/src/controllers/backup.controller.js: si el
// backup cambia, este texto cambia con él (hay una prueba que fija lo esencial).
import React from "react";
import InfoAyuda, { AyudaBloque } from "../../Components/InfoAyuda";

const lista: React.CSSProperties = { margin: "4px 0 0", paddingLeft: 20 };

const BackupAyuda: React.FC = () => (
  <InfoAyuda titulo="Cómo funciona el backup" texto="Cómo funciona" testId="ayuda-backup">
    <AyudaBloque titulo="Qué es">
      Aquí descargas y restauras copias de seguridad. Cada descarga es un archivo ZIP que siempre
      trae la base de datos completa y, si tú quieres, también las fotos, videos y documentos.
      Esta página es solo para el superadministrador.
    </AyudaBloque>

    <AyudaBloque titulo="Qué lleva cada descarga">
      <ul style={lista}>
        <li>
          <strong>Siempre:</strong> todas las tablas de la base de datos (una por archivo CSV), la
          firma de seguridad y un archivo <code>BACKUP_INFO.json</code> que declara si el ZIP trae
          o no los medios.
        </li>
        <li>
          <strong>Con «Incluir fotos, videos y documentos» activado:</strong> además, TODOS los
          archivos que hay en el volumen del sistema: los originales (fotos, videos y
          documentos), sus miniaturas y las versiones livianas de los videos. Bajo el interruptor
          verás la cuenta exacta, por ejemplo «11 originales + 10 miniaturas + 3 versiones
          livianas = 24 archivos · 23,2 MB»: es lo que el sistema encontró en el volumen y lo que
          meterá en el ZIP. El peso del archivo descargado queda muy cerca de esa cifra (las
          fotos y los videos ya vienen comprimidos, y las tablas suman unos pocos MB).
        </li>
        <li>
          <strong>No entran:</strong> los archivos antiguos que todavía estén alojados fuera del
          volumen (por ejemplo, algunos avatares viejos).
        </li>
      </ul>
    </AyudaBloque>

    <AyudaBloque titulo="Por qué no coincide con el número de /almacenamiento">
      En <strong>/almacenamiento</strong> una foto con su miniatura, o un video con su
      miniatura y su versión liviana, cuenta como <strong>un solo archivo</strong>: es lo que
      una persona ve y elige. El backup en cambio copia <strong>archivos físicos</strong>, así
      que cuenta el original, la miniatura y la versión liviana por separado. Por eso los 11
      archivos de /almacenamiento pueden ser 24 en el backup. El <strong>peso</strong>, en
      cambio, coincide en ambos lados. Si el volumen tiene algo que /almacenamiento no
      muestra (por ejemplo un archivo suelto fuera de las carpetas conocidas), aparece en el
      backup como «otros», y su peso se suma solo allí.
    </AyudaBloque>

    <AyudaBloque titulo="Con medios o sin medios">
      <ul style={lista}>
        <li>
          <strong>Sin medios:</strong> rápido y liviano. Es lo ideal para copias frecuentes de la
          base de datos. El archivo se llama <code>backup-AAAA-MM-DD-sin-medios.zip</code>.
        </li>
        <li>
          <strong>Con medios:</strong> la copia total, para recuperarse de un desastre. Con muchos
          videos puede pesar decenas de GB.
        </li>
      </ul>
      <div style={{ marginTop: 6 }}>
        La descarga pasa por tu navegador y el servicio corta las respuestas muy largas (alrededor
        de 15 minutos), así que un ZIP enorme puede no terminar de bajar. Si te pasa, descarga sin
        medios y respalda el volumen por otra vía. El sistema recuerda tu elección del interruptor.
      </div>
    </AyudaBloque>

    <AyudaBloque titulo="Al restaurar">
      <ul style={lista}>
        <li>
          <strong>Restauración completa:</strong> reemplaza todas las tablas por las del ZIP. No se
          puede deshacer y pide confirmación. Usa siempre el ZIP completo, nunca un subconjunto.
          Si el ZIP trae medios, cada archivo se compara con el inventario firmado antes de
          escribirse en el volumen; se reescriben los que trae el ZIP y no se borra ninguno que no
          venga. El resultado muestra cuántos archivos se restauraron, con las mismas clases que
          la descarga (originales, miniaturas, versiones livianas).
        </li>
        <li>
          <strong>Agregar/Editar:</strong> agrega o actualiza filas según su identificador, sin
          borrar nada. Las filas que ya existen con el mismo identificador se sobrescriben con los
          valores del ZIP, y no restaura archivos.
        </li>
      </ul>
    </AyudaBloque>

    <AyudaBloque titulo="Si restauras un ZIP sin medios">
      La base de datos queda bien restaurada. Los originales (fotos, videos y documentos), sus
      miniaturas y las versiones livianas que no estén ya en el volumen quedarán como
      referencias rotas: adjuntos que no se abren, visibles en{" "}
      <strong>/almacenamiento</strong>. Si restauras sobre el mismo volumen, o copiaste los
      archivos por tu cuenta, no se pierde nada. La pantalla de resultado lo avisa.
    </AyudaBloque>

    <AyudaBloque titulo="La firma de seguridad">
      Cada ZIP sale firmado. La firma cubre las tablas, el inventario de archivos (cuando trae
      medios) y <code>BACKUP_INFO.json</code>, la declaración de si trae medios. Si alguien cambia
      algo, o le agrega archivos, la restauración se rechaza antes de tocar la base de datos. Por
      eso solo sirven los ZIP descargados desde esta página; un CSV suelto solo se acepta en
      ambientes donde no hay firma configurada. Además, la clave de firma tiene que ser la misma
      con la que se creó el ZIP: si se cambió, los ZIP anteriores ya no se pueden restaurar.
    </AyudaBloque>

    <AyudaBloque titulo="Versión del sistema">
      Un backup nuevo debe restaurarse con el sistema ya actualizado. Uno más antiguo lo rechaza
      porque no reconoce la declaración de medios que va dentro de la firma. En general, restaura
      con una versión igual o más reciente que la que generó el ZIP.
    </AyudaBloque>

    <AyudaBloque titulo="Qué no hace y cuidados">
      <ul style={lista}>
        <li>
          No guarda nada en el servidor ni en una nube, y no programa copias: cada ZIP lo descarga
          y lo custodia una persona. Contiene datos personales de todos los clientes y las
          contraseñas cifradas, así que trátalo como una contraseña.
        </li>
        <li>
          Restaurar puede tardar varios minutos y no muestra barra de progreso. Si aparece un
          error, no reintentes de inmediato: primero revisa si la restauración llegó a aplicarse.
        </li>
        <li>
          Railway, donde está alojado el sistema, tiene además su propia opción de respaldos del
          volumen. Queda fuera de esta página: revisa su documentación para saber si te conviene y
          cómo se usa.
        </li>
      </ul>
    </AyudaBloque>
  </InfoAyuda>
);

export default BackupAyuda;
